// Config and hooks for the `markdown` sanitize preset in `@github-ui/sanitize`.
// Selected via `sanitize(html, {preset: 'markdown'})`; use it to sanitize rendered
// GitHub-Flavored Markdown (block content).
//
// The config is default-deny (from `SHARED_CONFIG`) plus the GitHub custom-element
// tags and attributes rendered markdown needs, with always-on link, image, class
// and data-* hardening applied through `addMarkdownSanitizeHooks`. Output is minted
// through the dedicated `github-sanitize-markdown` Trusted Types policy (see
// `trusted-types.ts`); a strict preset never reuses the lax `github-sanitize-lax`
// policy.

import type {Config, Purifier, UponSanitizeAttributeHookEvent} from '../types'
import {sanitizeNewWindowLinks, sanitizeNonGitHubImageLinks, SHARED_CONFIG} from './shared-hooks'

// `data-*` attributes that carry a value which may legitimately contain a colon
// (Catalyst `data-action="event:tag#method"`, titles/JSON/hosts), so DOMPurify's
// URI heuristic would otherwise strip them. `ADD_URI_SAFE_ATTR` opts them out of
// that heuristic — which ALSO bypasses DOMPurify's scheme scrubbing — so every one
// of these MUST be re-validated by `validateUriSafeDataAttr` below.
const URI_SAFE_DATA_ATTRS = new Set([
  'data-action',
  'data-item-title',
  'data-plain',
  'data-host',
  'data-content',
  'data-json',
])

// Markdown config — the GitHub custom-element tag baseline rendered markdown needs,
// layered on the shared default-deny base.
export const MARKDOWN_SANITIZE_CONFIG: Readonly<Config> = {
  ...SHARED_CONFIG,

  ADD_TAGS: [
    'action-menu',
    'anchored-position',
    'auto-complete',
    'focus-group',
    'g-emoji',
    'tracking-block',
    'tracking-block-omnibar',
    'tasklist-block-title',
    'details-menu',
    // GH math expressions
    'math-renderer',
    // classic task lists support
    'task-lists',
    // used by unfurled labels
    'tool-tip',
    'turbo-frame',
    'modal-dialog',
    // Used by tasklist-block-title
    'primer-text-field',
  ],
  // The functional allowlist re-permitted on top of the shared default-deny
  // (`ALLOW_DATA_ATTR: false`) posture: only the specific non-`data-*` and `data-*`
  // attributes rendered markdown legitimately needs. Ported from memex's
  // RENDERED_MARKDOWN config, deliberately CONSERVATIVE: behavior-binding markers
  // the threat model flags (e.g. `data-src`, `data-url`, `data-channel`) are NOT
  // allowlisted, so they fall through to default-deny and are stripped.
  ADD_ATTR: [
    // attributes for g-emoji components
    'alias',
    'anchor',
    'anchor-offset',
    'fallback-src',
    // attributes for hovercard attributes attached to anchor tags
    'data-hovercard-type',
    'data-hovercard-url',
    'data-octo-click',
    'data-octo-dimensions',
    // attributes for mermaidjs rich rendering (note: behavior-binding `data-src`
    // is intentionally omitted — see the ADD_ATTR doc comment above)
    'data-identity',
    'data-host',
    'data-content',
    'data-type',
    'data-json',
    'data-plain',
    // attributes for tracking-block
    'data-id',
    'data-issue',
    'data-draft-issue',
    'data-item-uuid',
    'data-item-id',
    'data-item-title',
    'data-item-state',
    'data-item-position',
    'data-menu-type',
    'data-repository-id',
    'data-repository-name',
    'data-show-dialog-id',
    'data-close-dialog-id',
    'data-modal-dialog-overlay',
    'data-display-number',
    'data-hydro-click-hmac',
    'data-hydro-click-payload',
    'data-hydro-click',
    'data-tracking-block-item-title',
    'data-tracking-block-convert-button',
    'data-tracking-block-remove-button',
    'data-tracking-block-draft-title',
    'data-form-tracking-block-update',
    'data-completion-completed',
    'data-completion-total',
    'data-response-source-type',
    'data-query-type',
    'data-precache',
    // attributes for task-lists
    'sortable',
    // attributes for catalyst
    'data-view-component',
    'data-catalyst',
    'data-action',
    'data-target',
    'data-targets',
    // attributes for enriched video element rendered in issue body or comments
    'data-canonical-src',
    // attributes for mathjax
    'mnemonics',
    'popover',
    'popovertarget',
    'retain',
    'side',
  ],
  // Colon-bearing `data-*` markers DOMPurify would otherwise treat as URIs and
  // strip. Opting them out of the URI heuristic bypasses scheme scrubbing, so
  // `validateUriSafeDataAttr` re-applies dangerous-scheme rejection to each.
  ADD_URI_SAFE_ATTR: [...URI_SAFE_DATA_ATTRS],
  // Extra strictness on the rendered path: no inline style, no `hidden`, no `id`.
  // `id` is default-denied here (never allowlisted) to close DOM-clobbering.
  FORBID_ATTR: ['style', 'hidden', 'id'],
}

// ── Class hook — the markdown preset's `js-*`-only denylist (#1728) ────────────

/**
 * Strip the `js-*` class tokens GitHub's client behaviors bind to (selector
 * observers like `observe('.js-...')`), leaving every other class intact. A
 * `js-*`-only denylist rather than a wholesale class allowlist: every confirmed
 * bug-bounty payload (github/web-systems#4809) weaponizes a `js-*` hook, so this
 * neutralizes them while preserving legitimate rendered-markdown classes (syntax
 * highlighting like `highlight`/`blob-code`/`pl-*`, etc.).
 *
 * Class values are tokenized on ASCII whitespace exactly as the browser does
 * (space, tab, LF, FF, CR), so tab/newline/FF/CR-obfuscated `js-*` tokens are
 * isolated the same way a mutation-observer gadget would see them and cannot slip
 * through. The `js-` prefix match is case-insensitive for defense in depth.
 */
const stripJsHookClasses = (_node: Element, data: UponSanitizeAttributeHookEvent): void => {
  if (data.attrName !== 'class') return

  const kept = data.attrValue.split(/[ \t\n\f\r]+/).filter(token => token !== '' && !/^js-/i.test(token))
  if (kept.length === 0) {
    data.keepAttr = false
  } else {
    data.attrValue = kept.join(' ')
  }
}

// ── Attribute hook — validate URI-safe `data-*` values (#1724) ─────────────────

// ASCII control chars + Unicode whitespace DOMPurify strips before scheme testing;
// mirrors DOMPurify's own `ATTR_WHITESPACE` so an obfuscated scheme (leading NULs,
// tabs, newlines) cannot evade the check.
// eslint-disable-next-line no-control-regex
const ATTR_WHITESPACE = /[\u0000-\u0020\u00a0\u1680\u180e\u2000-\u2029\u205f\u3000]/g

// Dangerous URL schemes DOMPurify normally scrubs. Mirrors DOMPurify's
// `IS_SCRIPT_OR_DATA`, extended with `vbscript`.
const IS_DANGEROUS_SCHEME = /^(?:\w+script|data|vbscript):/i

/**
 * Re-apply dangerous-scheme rejection to the `data-*` attributes we opt out of
 * DOMPurify's URI heuristic via `ADD_URI_SAFE_ATTR`. That opt-out also bypasses
 * DOMPurify's scheme scrubbing, so without this a payload like
 * `data-content="javascript:…"` would survive and could be read back into a script
 * sink by a client behavior. The value is whitespace/control-stripped and lowercased
 * (matching DOMPurify) before testing its leading scheme; a match drops the attribute.
 * Legitimate values for these markers (Catalyst actions, titles, JSON, hostnames)
 * never lead with `javascript:`/`data:`/`vbscript:`, so this rejects only abuse.
 */
const validateUriSafeDataAttr = (_node: Element, data: UponSanitizeAttributeHookEvent): void => {
  if (!URI_SAFE_DATA_ATTRS.has(data.attrName)) return

  const normalized = data.attrValue.replace(ATTR_WHITESPACE, '').toLowerCase()
  if (IS_DANGEROUS_SCHEME.test(normalized)) {
    data.keepAttr = false
  }
}

/**
 * Attach the markdown preset's security hooks to a purifier instance: always-on
 * link hardening, image-domain restriction, the `js-*` class denylist, and the
 * URI-safe `data-*` validator. The image hook guards on browser globals so the
 * server (linkedom) path is a safe no-op for it; the link hook runs on both client
 * and server (linkedom exposes `target`). Called from each entry's local
 * `createPurifier`.
 */
export function addMarkdownSanitizeHooks(instance: Purifier): void {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('afterSanitizeElements', sanitizeNonGitHubImageLinks)
  instance.addHook('uponSanitizeAttribute', stripJsHookClasses)
  instance.addHook('uponSanitizeAttribute', validateUriSafeDataAttr)
}
